import { Model } from '@vuex-orm/core'
import Store from '@/store/'
import AvailabilityModel from './AvailabilityModel'
import CustomerModel from './CustomerModel'
import ShopModel from './ShopModel'
import LineItemModel from './LineItemModel'
import FieldTypes from '@/constants/FieldTypes'
import _upperFirst from 'lodash/upperFirst'
import _get from 'lodash/get'
import { format, parseISO } from 'date-fns'
import { utcToLocal } from '@/utils/utcConverter'
import * as TimeFormats from '@/constants/TimeFormats'
import addClassesToModelHeaders from '@/utils/addClassesToModelHeaders'

export default class OrderModel extends Model {
  static entity = 'orders'

  static nameSingular = 'Order'
  static namePlural = 'Orders'

  static fields() {
    return {
      id: this.uid(),
      ticket_number: this.number(),
      completed: this.boolean(),
      ready_for_guest: this.boolean(),
      paid: this.boolean(),
      fulfillment_slot: this.string(''),
      fulfillment_type: this.string(''),
      delivery_address: this.string('').nullable(),
      special_instructions: this.attr(null),
      is_gift: this.boolean(),
      giftee_details: this.string('').nullable(),
      created_at: this.attr(null),
      user_id: this.attr(null),
      customer: this.belongsTo(CustomerModel, 'user_id'),
      availability_id: this.attr(null),
      availability: this.belongsTo(AvailabilityModel, 'availability_id'),
      line_items: this.hasMany(LineItemModel, 'order_id'),
      delivery_fee: this.number(0),
      service_fee: this.number(0),
      tax: this.number(0),
      tip: this.number(0),
      subtotal: this.number(0),
      grand_total: this.number(0),
      shop_id: this.attr(null),
      shop: this.belongsTo(ShopModel, 'shop_id'),
    }
  }

  static field_headers() {
    let headers = [
      {
        text: 'Tkt #',
        align: 'start',
        sortable: true,
        value: 'ticket_number',
        width: 60,
        type: FieldTypes.READONLY,
        hideOnDetail: true,
        sticky: true,
      },
      {
        text: 'Customer',
        value: 'customer.full_name',
        type: FieldTypes.BELONGS_TO,
        sortable: true,
        sortKey: 'customer_id',
        foreignModel: CustomerModel,
        sticky: true,
      },
      {
        text: 'Completed',
        align: 'center',
        value: 'completed',
        type: FieldTypes.BOOLEAN,
      },
      {
        text: 'Paid',
        value: 'paid',
        align: 'center',
        sortable: true,
        type: FieldTypes.BOOLEAN,
      },
      {
        text: 'Ready',
        value: 'ready_for_guest',
        align: 'center',
        type: FieldTypes.BOOLEAN,
      },
      {
        text: 'Gift?',
        value: 'is_gift',
        align: 'center',
        type: FieldTypes.TEXT,
      },
      {
        text: 'Availability',
        value: 'availability.name',
        type: FieldTypes.BELONGS_TO,
        sortable: true,
        sortKey: 'availability',
        foreignModel: AvailabilityModel,
        localKey: 'availability_id',
      },
      {
        text: 'Total Cost',
        value: 'grand_total_formatted',
        type: FieldTypes.MONEY,
        align: 'right',
        width: 100,
        readonly: true,
        hideOnDetail: true,
      },
      {
        text: 'Fulfillment Type',
        value: 'fulfillment_type',
        width: 120,
        sortable: true,
        type: FieldTypes.SELECT,
      },
      {
        text: 'Fulfillment Slot',
        value: 'fulfillment_slot',
        width: 200,
        sortable: true,
        type: FieldTypes.SELECT,
      },
      {
        text: 'Email',
        value: 'customer.email',
        foreignModel: CustomerModel,
        localKey: 'user_id',
      },
      {
        text: 'Delivery Address',
        value: 'delivery_address',
        align: 'center',
        type: FieldTypes.TEXT,
      },
      {
        text: 'Time Ordered',
        value: 'time_ordered',
        width: 160,
        readonly: true,
        hideOnDetail: true,
      },
      {
        text: 'Notes',
        value: 'special_instructions',
        align: 'center',
        type: FieldTypes.READONLY,
      },
    ]
    return addClassesToModelHeaders(headers)
  }

  static mutators() {
    return {
      fulfillment_type(type) {
        return type ? _upperFirst(type) : ''
      },
    }
  }

  static index_table_config() {
    return {
      exportable: true,
    }
  }

  static index_quick_links() {
    return [
      {
        title: 'Incomplete',
        query: { filters: { completed: 0 } },
        default: true,
      },
      {
        title: 'Completed',
        query: { filters: { completed: 1 } },
      },
      {
        title: 'Unpaid',
        query: { filters: { paid: 0 } },
      },
      {
        title: 'Paid',
        query: { filters: { paid: 1 } },
      },
      {
        title: 'Gifts',
        query: { filters: { is_gift: 1 } },
      },
    ]
  }

  static index_query_with() {
    return 'customer'
  }
  static detail_query_with() {
    return 'availability'
  }

  get time_ordered() {
    let localTime = utcToLocal(this.created_at)
    return format(parseISO(localTime), TimeFormats.dateAndTimeSlashes12hr)
  }

  get time_ordered_for_invoice() {
    let localTime = utcToLocal(this.created_at)
    return format(parseISO(localTime), TimeFormats.verbalDateAndTimeAt)
  }

  /**
   * Get subtotal adjusted in USD (cents x 100)
   */
  get subtotal_usd() {
    return this.subtotal / 100
  }

  /**
   * Get subtotal formatted like a $D,DDD.CC
   */
  get subtotal_formatted() {
    return this.subtotal ? `$${this.subtotal_usd.toFixed(2)}` : 'No subtotal.'
  }

  /**
   * Get tax adjusted in USD (cents x 100)
   */
  get tax_usd() {
    return this.tax / 100
  }

  /**
   * Get tax formatted like a $D,DDD.CC
   */
  get tax_formatted() {
    return this.tax ? `$${this.tax_usd.toFixed(2)}` : 'No tax.'
  }

  /**
   * Get tip adjusted in USD (cents x 100)
   */
  get tip_usd() {
    return this.tip / 100
  }

  /**
   * Get tip formatted like a $D,DDD.CC
   */
  get tip_formatted() {
    return this.tip ? `$${this.tip_usd.toFixed(2)}` : 'No tip.'
  }

  /**
   * Get service fee adjusted in USD (cents x 100)
   */
  get service_fee_usd() {
    return this.service_fee / 100
  }

  /**
   * Get Service Fee like a $D,DDD.CC
   */
  get service_fee_formatted() {
    return this.service_fee ? `$${this.service_fee_usd.toFixed(2)}` : ''
  }

  /**
   * Get grand_total adjusted in USD (cents x 100)
   */
  get grand_total_usd() {
    return this.grand_total / 100
  }

  /**
   * Get grand_total formatted like a $D,DDD.CC
   */
  get grand_total_formatted() {
    return this.grand_total ? `$${this.grand_total_usd.toFixed(2)}` : ' – '
  }

  get availability_name() {
    return _get(this, 'availability.name', '')
  }
  get customer_first_name() {
    return this.customer && this.customer.first_name
  }

  get customer_last_name() {
    return this.customer && this.customer.last_name
  }

  get customer_full_name() {
    return [this.customer_first_name, this.customer_last_name].join(' ')
  }

  get customer_phone() {
    return this.customer && this.customer.phone
  }

  get customer_email() {
    return this.customer && this.customer.email
  }

  get products() {
    return this.line_items.filter(
      (li) => li.type === LineItemModel.lineItemTypes.PRODUCT
    )
  }
  get fees() {
    let fees = this.line_items.filter(
      (li) => li.type === LineItemModel.lineItemTypes.FEE
    )
    if (this.delivery_fee) {
      const prefix = this.item_price_usd < 0 ? '-$' : '$'
      const item_price_formatted = `${prefix}${Math.abs(
        this.delivery_fee / 100
      ).toFixed(2)}`

      fees.push({
        item_name: 'Delivery Fee',
        item_price_formatted,
      })
    }
    return fees
  }
  get discounts() {
    return this.line_items.filter(
      (li) => li.type === LineItemModel.lineItemTypes.DISCOUNT
    )
  }

  get gift_details_object() {
    return JSON.parse(this.giftee_details)
  }

  static fetchForCustomer(customerId) {
    return this.api().get(`/customers/${customerId}/orders`, {
      dataTransformer: (response) => {
        return response.data.data
      },
    })
  }

  static findForCustomer(customerId, orderId) {
    return this.api().get(`/customers/${customerId}/orders/${orderId}`, {
      dataTransformer: (response) => {
        return response.data.data
      },
    })
  }

  static createShopOrder(orderData) {
    let shopId = Store.getters['shop/getShopId']
    return this.api().post(`/shops/${shopId}/orders`, orderData, {
      dataTransformer: (response) => {
        return response.data.data
      },
    })
  }
  static fetchOrderPrepForAvailabilities(availabilities) {
    let shopId = Store.getters['shop/getShopId']
    // TODO: figure out how to use dataTransformer to avoid plucking data from response
    return this.api().get(`/shops/${shopId}/orders/prep`, {
      params: { availabilities },
    })
  }
  static fetchOrderPrepByDate(date) {
    let shopId = Store.getters['shop/getShopId']
    // TODO: figure out how to use dataTransformer to avoid plucking data from response
    return this.api().get(`/shops/${shopId}/orders/prep`, {
      params: { date },
    })
  }
}
